/**
 * asArray — safely coerce an API response into an array for list state.
 *
 * WHY THIS EXISTS (R138, R145):
 * The codebase repeatedly used the fallback chain `response-optional-key OR
 * response OR empty-array` to accept both an enveloped response (`{items:
 * [...]}`) and a bare array. That chain is a trap: when the envelope key is
 * MISSING (renamed field, different service, error envelope), the optional
 * access yields undefined and the middle arm substitutes the ENVELOPE OBJECT
 * ITSELF into array state. The component then calls `.map()` / `.filter()` on
 * an object and throws a TypeError — typically in a render line nowhere near
 * the assignment, which is why these took so long to diagnose. Three separate
 * rounds fixed instances of it one at a time; this helper replaces all of them.
 *
 * This helper can only ever return an array. Pass the expected envelope keys in
 * priority order; `items` / `results` / `data` are tried as a last resort.
 *
 * It deliberately LOGS when it drops a non-array payload — a guard that
 * degrades silently turns a contract break into a believable empty state.
 */
const COMMON_KEYS = ['items', 'results', 'data'];

export function asArray<T = any>(value: any, ...keys: string[]): T[] {
  if (Array.isArray(value)) return value as T[];
  // A JSONB column that the service handed back undecoded arrives as raw JSON
  // TEXT (asyncpg returns jsonb as str). `access_keys` reached the browser as
  // the string "[]", and because a non-empty string is truthy, `x || []` let it
  // straight through into `.map()`. Parse it here so every existing caller of
  // this helper is covered, not just the one page that happened to crash.
  if (typeof value === 'string') {
    const s = value.trim();
    if (s && (s[0] === '[' || s[0] === '{')) {
      try {
        return asArray<T>(JSON.parse(s), ...keys);
      } catch {
        return [];
      }
    }
    return [];
  }
  if (value && typeof value === 'object') {
    for (const k of keys) {
      if (Array.isArray((value as any)[k])) return (value as any)[k] as T[];
    }
    for (const k of COMMON_KEYS) {
      if (keys.includes(k)) continue;
      if (Array.isArray((value as any)[k])) return (value as any)[k] as T[];
    }
    // An object arrived where a list was expected and none of the candidate
    // keys held an array. Say so — this is a backend/frontend contract break,
    // not an empty result.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        '[asArray] expected an array or an envelope with one of',
        [...keys, ...COMMON_KEYS],
        '— got object with keys:',
        Object.keys(value as any).slice(0, 12),
      );
    }
  }
  return [];
}

export default asArray;
